/**
 * BusinessLandingPage — /business
 * ────────────────────────────────────────────────────────────────────
 * The public marketing / onboarding entry for «كرتال أعمال | CARTAL Business».
 *
 * DOES NOT touch or replace any existing marketplace surface. The page
 * is purely additive and links out to `/businesses/new` (registration)
 * and `/businesses` (workspace list) which already exist.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  CreditCard,
  Gauge,
  LayoutDashboard,
  ShieldCheck,
  Sparkles,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";
import { apiClient } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";

/* Sector illustrations grouped for a visual grid — each ties to a real
   BUSINESS_CATEGORIES key so the copy stays in sync with the seed. */
const SECTORS = [
  { key: "dealer",              icon: Building2,      ar: "معارض ووكالات",           en: "Dealers & Agencies" },
  { key: "workshop",            icon: Wrench,         ar: "ورش الصيانة",              en: "Workshops" },
  { key: "rental_company",      icon: LayoutDashboard, ar: "تأجير السيارات",          en: "Car Rentals" },
  { key: "insurance_company",   icon: ShieldCheck,    ar: "التأمين والوسطاء",         en: "Insurance & Brokers" },
  { key: "bank",                icon: Wallet,         ar: "البنوك والتمويل",          en: "Banks & Finance" },
  { key: "fintech",             icon: Sparkles,       ar: "التقنية المالية والـBNPL", en: "FinTech & BNPL" },
  { key: "parts_store",         icon: Briefcase,      ar: "قطع الغيار والإطارات",     en: "Parts & Tires" },
  { key: "inspection_center",   icon: ClipboardCheck, ar: "الفحص والعناية",           en: "Inspection & Detailing" },
];

const FEATURES = [
  {
    icon: Gauge,
    ar: {
      title: "لوحة تحكم ديناميكية",
      body: "كل نوع نشاط يحصل على لوحة مخصصة: المخزون للمعارض، الحجوزات للورش، طلبات التمويل للبنوك، وخطط التقسيط لخدمات BNPL.",
    },
    en: {
      title: "Dynamic Dashboard",
      body: "Every business type gets a tailored workspace — inventory for dealers, bookings for workshops, finance requests for banks, installment plans for BNPL.",
    },
  },
  {
    icon: Building2,
    ar: {
      title: "فروع وموظفون بلا حدود",
      body: "أضِف فروعك في كل مدينة، ادعُ موظفيك، وحدّد صلاحياتهم بمرونة (مالك، مدير فرع، موظف، مشاهد).",
    },
    en: {
      title: "Branches & Staff",
      body: "Add branches across cities, invite staff, and assign granular roles: Owner, Branch Manager, Employee, Viewer.",
    },
  },
  {
    icon: ShieldCheck,
    ar: {
      title: "توثيق رسمي",
      body: "ارفع سجلك التجاري وشهاداتك؛ وسم «موثّق» يظهر على ملفك العام ويرفع الثقة والتحويل.",
    },
    en: {
      title: "Official Verification",
      body: "Upload your commercial registration and licenses. A “Verified” badge appears on your public profile and boosts trust.",
    },
  },
  {
    icon: Users,
    ar: {
      title: "دليل عام مع تقييمات",
      body: "ملف عام احترافي قابل للمشاركة، متكامل مع نظام الرسائل والتقييمات — يحوّل الزوار إلى عملاء.",
    },
    en: {
      title: "Public Directory & Reviews",
      body: "A shareable public profile connected to messaging and reviews — turns visitors into customers.",
    },
  },
  {
    icon: CreditCard,
    ar: {
      title: "اشتراكات جاهزة للنمو",
      body: "ابدأ مجانًا الآن. سيتم لاحقًا تفعيل الخطط الاحترافية شهرية وسنوية عبر Moyasar بضغطة واحدة.",
    },
    en: {
      title: "Growth-Ready Plans",
      body: "Start free today. Monthly and annual paid plans via Moyasar will unlock advanced tools as you scale.",
    },
  },
  {
    icon: Sparkles,
    ar: {
      title: "نتاج نظام كرتال الكامل",
      body: "بيانات موحّدة مع السوق، الرسائل، التنبيهات، والمدفوعات — بدون تكرار أو تكامل خارجي.",
    },
    en: {
      title: "One Unified Platform",
      body: "Shared users, messages, notifications, and payments across the whole CARTAL platform — no duplicate integrations.",
    },
  },
];

const PLAN_FEATURES_FREE = [
  { ar: "ملف تعريفي عام + وسم النشاط", en: "Public profile with category badge" },
  { ar: "لوحة تحكم كاملة حسب نوع النشاط", en: "Full dashboard tailored to your type" },
  { ar: "حتى فرع واحد و ٣ موظفين", en: "Up to 1 branch & 3 staff members" },
  { ar: "استقبال طلبات العملاء والرسائل", en: "Receive customer requests & messages" },
  { ar: "توثيق أساسي عبر السجل التجاري", en: "Basic verification via CR document" },
];

const PLAN_FEATURES_PRO = [
  { ar: "كل ما في المجاني +", en: "Everything in Free, plus:" },
  { ar: "فروع وموظفون بلا حدود", en: "Unlimited branches & staff" },
  { ar: "تحليلات متقدمة + تقارير", en: "Advanced analytics & reports" },
  { ar: "أولوية الظهور في الدليل العام", en: "Priority listing in public directory" },
  { ar: "شارة «موثّق مميز» + دعم مخصص", en: "Premium verified badge + dedicated support" },
  { ar: "خصومات على العروض الترويجية", en: "Discounts on promotion packages" },
];

export default function BusinessLandingPage() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    apiClient
      .get("/platform/categories")
      .then(({ data }) => setCategories(data.items || []))
      .catch(() => {});
    // Public directory stats are optional — fail silent if endpoint absent.
    apiClient
      .get("/platform/public/directory/stats")
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, []);

  const primaryCta = user ? "/businesses/new" : "/register?next=/businesses/new";
  const secondaryCta = "/companies";

  return (
    <div className="bg-[#fafaf7]" data-testid="business-landing-page">
      {/* ─── Hero ─────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(160deg,#0a0a0a 0%,#141110 45%,#1f1614 100%)",
        }}
      >
        {/* Ambient glow */}
        <div aria-hidden className="absolute inset-0 opacity-30 pointer-events-none">
          <div
            className="absolute -top-40 -end-40 w-[520px] h-[520px] rounded-full blur-3xl"
            style={{ background: "#c9a55c" }}
          />
          <div
            className="absolute -bottom-40 -start-40 w-[520px] h-[520px] rounded-full blur-3xl"
            style={{ background: "#8a6f2e" }}
          />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 pt-16 sm:pt-24 pb-20 sm:pb-28">
          <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
            <div>
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold mb-6"
                style={{
                  background: "rgba(201,165,92,0.12)",
                  border: "1px solid rgba(201,165,92,0.35)",
                  color: "#e8c873",
                }}
              >
                <Sparkles className="w-3.5 h-3.5" />
                {lang === "ar" ? "جديد · للأعمال" : "New · For Business"}
              </div>

              <h1
                className="font-display font-black tracking-tight text-[#f7eecd] leading-[1.05] mb-5"
                style={{ fontSize: "clamp(2.25rem, 5.2vw, 4.25rem)" }}
              >
                {lang === "ar" ? (
                  <>
                    كرتال <span style={{ color: "#e8c873" }}>أعمال</span>
                    <br />
                    <span className="text-[0.55em] font-medium text-[#f7eecd]/70">
                      المنظومة الرقمية الأولى لقطاع المركبات في المملكة
                    </span>
                  </>
                ) : (
                  <>
                    CARTAL <span style={{ color: "#e8c873" }}>Business</span>
                    <br />
                    <span className="text-[0.55em] font-medium text-[#f7eecd]/70">
                      Saudi Arabia&apos;s premier B2B ecosystem for the mobility sector
                    </span>
                  </>
                )}
              </h1>

              <p
                className="text-[#f7eecd]/80 max-w-xl mb-8 leading-relaxed"
                style={{ fontSize: "clamp(0.95rem, 1.3vw, 1.125rem)" }}
              >
                {lang === "ar"
                  ? "منصة B2B متكاملة للمعارض، الورش، شركات التأجير، التأمين، البنوك، التقنية المالية، وخدمات BNPL — لوحة تحكم ديناميكية، فروع وموظفون، توثيق رسمي، ودليل عام يوصلك بعملائك."
                  : "A unified B2B platform for dealers, workshops, rentals, insurers, banks, FinTech, and BNPL providers — dynamic dashboards, branches & staff, official verification, and a public directory that connects you to customers."}
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to={primaryCta}
                  className="inline-flex items-center gap-2 rounded-full h-12 px-6 text-sm font-bold shadow-xl hover:scale-[1.02] transition-transform"
                  style={{
                    background: "linear-gradient(135deg,#e8c873,#c9a55c)",
                    color: "#0a0a0a",
                  }}
                  data-testid="cta-register-business"
                >
                  {lang === "ar" ? "ابدأ الآن مجانًا" : "Get started free"}
                  <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
                </Link>
                <Link
                  to={secondaryCta}
                  className="inline-flex items-center gap-2 rounded-full h-12 px-6 text-sm font-semibold border transition-colors"
                  style={{
                    borderColor: "rgba(247,238,205,0.25)",
                    color: "#f7eecd",
                  }}
                  data-testid="cta-view-directory"
                >
                  {lang === "ar" ? "استكشف دليل الأعمال" : "Explore directory"}
                </Link>
              </div>

              {user && (
                <div className="mt-6">
                  <Link
                    to="/businesses"
                    className="text-[13px] text-[#e8c873] hover:underline underline-offset-4"
                    data-testid="cta-my-workspaces"
                  >
                    {lang === "ar"
                      ? "لديك حساب أعمال؟ افتح مساحاتي →"
                      : "Have a workspace? Open mine →"}
                  </Link>
                </div>
              )}
            </div>

            {/* Right visual — sector grid */}
            <div className="relative">
              <div
                className="rounded-3xl p-6 sm:p-8 border backdrop-blur-md"
                style={{
                  background:
                    "linear-gradient(135deg,rgba(201,165,92,0.10) 0%,rgba(247,238,205,0.03) 100%)",
                  borderColor: "rgba(201,165,92,0.25)",
                }}
              >
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#c9a55c] mb-4">
                  {lang === "ar" ? "قطاعات مدعومة" : "Supported sectors"}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {SECTORS.map((s) => (
                    <div
                      key={s.key}
                      className="rounded-2xl p-4 flex items-start gap-3 border transition-colors hover:bg-white/[0.06]"
                      style={{
                        background: "rgba(247,238,205,0.04)",
                        borderColor: "rgba(247,238,205,0.10)",
                      }}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                        style={{
                          background:
                            "linear-gradient(135deg,rgba(232,200,115,0.20),rgba(201,165,92,0.10))",
                          color: "#e8c873",
                        }}
                      >
                        <s.icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-[#f7eecd] leading-tight">
                          {lang === "ar" ? s.ar : s.en}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-[11px] text-[#f7eecd]/60">
                  {lang === "ar"
                    ? `+${(categories.length || 22)} فئة نشاط قابلة للتوسّع من لوحة الإدارة.`
                    : `+${(categories.length || 22)} categories, extensible from the admin panel.`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Trust strip ─────────────────────────────────────────── */}
      {stats && (stats.total_verified_businesses || stats.total_businesses) && (
        <section className="border-b border-[#0a0a0a]/[0.06]">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-8 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-display font-black text-[#0a0a0a]">
                {stats.total_businesses || 0}+
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#8a6f2e] font-semibold mt-1">
                {lang === "ar" ? "أنشطة مسجّلة" : "Businesses"}
              </div>
            </div>
            <div>
              <div className="text-3xl font-display font-black text-[#0a0a0a]">
                {stats.total_verified_businesses || 0}
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#8a6f2e] font-semibold mt-1">
                {lang === "ar" ? "أنشطة موثّقة" : "Verified"}
              </div>
            </div>
            <div>
              <div className="text-3xl font-display font-black text-[#0a0a0a]">
                {categories.length || 22}
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#8a6f2e] font-semibold mt-1">
                {lang === "ar" ? "فئات نشاط" : "Categories"}
              </div>
            </div>
            <div>
              <div className="text-3xl font-display font-black text-[#0a0a0a]">
                24/7
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#8a6f2e] font-semibold mt-1">
                {lang === "ar" ? "منصة حيّة" : "Always on"}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── Features ─────────────────────────────────────────────── */}
      <section className="py-16 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#8a6f2e] font-bold mb-3">
              {lang === "ar" ? "لماذا كرتال أعمال" : "Why CARTAL Business"}
            </div>
            <h2 className="text-3xl sm:text-5xl font-display font-black text-[#0a0a0a] tracking-tight leading-tight">
              {lang === "ar"
                ? "منظومة واحدة تدير عملك بالكامل"
                : "One system to run your entire operation"}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <div
                key={i}
                className="rounded-2xl p-6 bg-white border border-[#0a0a0a]/[0.06] hover:shadow-lg hover:-translate-y-0.5 transition-all"
                data-testid={`feature-card-${i}`}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{
                    background:
                      "linear-gradient(135deg,#f7eecd,#e8c873)",
                    color: "#0a0a0a",
                  }}
                >
                  <f.icon className="w-5 h-5" />
                </div>
                <div className="font-display font-bold text-[#0a0a0a] text-lg mb-1.5">
                  {lang === "ar" ? f.ar.title : f.en.title}
                </div>
                <div className="text-[13px] leading-relaxed text-[#525252]">
                  {lang === "ar" ? f.ar.body : f.en.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Plans ────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-white border-y border-[#0a0a0a]/[0.06]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#8a6f2e] font-bold mb-3">
              {lang === "ar" ? "الخطط" : "Plans"}
            </div>
            <h2 className="text-3xl sm:text-5xl font-display font-black text-[#0a0a0a] tracking-tight mb-3">
              {lang === "ar" ? "ابدأ مجانًا. انمُ عند الجاهزية." : "Start free. Upgrade when ready."}
            </h2>
            <p className="text-[#525252] text-[15px]">
              {lang === "ar"
                ? "نبني معك خطواتك الأولى بدون تكلفة. الخطط الاحترافية ستُفعّل قريبًا عبر Moyasar."
                : "We take the first step with you at no cost. Pro plans will unlock soon via Moyasar."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {/* Free */}
            <div className="rounded-3xl p-7 border border-[#0a0a0a]/[0.08] bg-[#fafaf7] flex flex-col">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#8a8a8a] mb-3">
                {lang === "ar" ? "المجاني" : "Free"}
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <div className="text-5xl font-display font-black text-[#0a0a0a]">0</div>
                <div className="text-sm text-[#525252]">{lang === "ar" ? "ر.س / شهر" : "SAR / mo"}</div>
              </div>
              <div className="text-[12px] text-[#525252] mb-6">
                {lang === "ar" ? "لكل الأنشطة الناشئة والفرق الصغيرة." : "For starters and small teams."}
              </div>
              <ul className="space-y-2.5 text-[13px] text-[#0a0a0a] mb-8 flex-1">
                {PLAN_FEATURES_FREE.map((p) => (
                  <li key={p.ar} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#c9a55c] shrink-0 mt-0.5" />
                    <span>{lang === "ar" ? p.ar : p.en}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={primaryCta}
                className="w-full inline-flex items-center justify-center rounded-full h-11 text-sm font-bold border-2 border-[#0a0a0a] text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-[#f7eecd] transition-colors"
                data-testid="cta-plan-free"
              >
                {lang === "ar" ? "ابدأ مجانًا" : "Start free"}
              </Link>
            </div>

            {/* Pro (Coming Soon) */}
            <div
              className="relative rounded-3xl p-7 border-2 flex flex-col overflow-hidden"
              style={{
                borderColor: "#c9a55c",
                background:
                  "linear-gradient(160deg,rgba(232,200,115,0.10) 0%,rgba(255,255,255,1) 60%)",
              }}
            >
              <div
                className="absolute top-4 end-4 text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full"
                style={{ background: "#0a0a0a", color: "#e8c873" }}
              >
                {lang === "ar" ? "قريبًا" : "Coming soon"}
              </div>
              <div
                className="text-[11px] uppercase tracking-[0.22em] font-bold mb-3"
                style={{ color: "#8a6f2e" }}
              >
                {lang === "ar" ? "الاحترافية" : "Pro"}
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <div className="text-5xl font-display font-black text-[#0a0a0a]">—</div>
                <div className="text-sm text-[#525252]">
                  {lang === "ar" ? "خطط شهرية وسنوية عبر Moyasar" : "Monthly & annual via Moyasar"}
                </div>
              </div>
              <div className="text-[12px] text-[#525252] mb-6">
                {lang === "ar" ? "للفرق التي تنمو بسرعة وتحتاج أدوات أعمق." : "For teams that need deeper tools."}
              </div>
              <ul className="space-y-2.5 text-[13px] text-[#0a0a0a] mb-8 flex-1">
                {PLAN_FEATURES_PRO.map((p) => (
                  <li key={p.ar} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#c9a55c] shrink-0 mt-0.5" />
                    <span>{lang === "ar" ? p.ar : p.en}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled
                className="w-full inline-flex items-center justify-center rounded-full h-11 text-sm font-bold opacity-70 cursor-not-allowed"
                style={{
                  background: "linear-gradient(135deg,#e8c873,#c9a55c)",
                  color: "#0a0a0a",
                }}
                data-testid="cta-plan-pro"
              >
                {lang === "ar" ? "قريبًا" : "Coming soon"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── How it works ─────────────────────────────────────────── */}
      <section className="py-16 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#8a6f2e] font-bold mb-3">
              {lang === "ar" ? "كيف تبدأ" : "How it works"}
            </div>
            <h2 className="text-3xl sm:text-5xl font-display font-black text-[#0a0a0a] tracking-tight">
              {lang === "ar" ? "٣ خطوات، أقل من ٥ دقائق" : "3 steps, under 5 minutes"}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                num: "01",
                ar: "سجّل النشاط",
                arBody: "اختر نوع نشاطك من القائمة، أضف الاسم بالعربية والإنجليزية، وأنشئ مساحة العمل.",
                en: "Register your business",
                enBody: "Pick your category, add your name in Arabic & English, and create your workspace.",
              },
              {
                num: "02",
                ar: "أضف فروعك وموظفيك",
                arBody: "أنشئ فروع متعددة، ادعُ موظفيك، وحدّد صلاحياتهم عبر أدوار جاهزة.",
                en: "Add branches & staff",
                enBody: "Create multiple branches, invite teammates, and assign predefined roles.",
              },
              {
                num: "03",
                ar: "استقبل العملاء",
                arBody: "يظهر نشاطك في الدليل العام. تصلك الرسائل والطلبات مباشرة داخل لوحة التحكم.",
                en: "Start winning customers",
                enBody: "Your business appears in the public directory. Messages and requests land right in your dashboard.",
              },
            ].map((s) => (
              <div
                key={s.num}
                className="rounded-2xl p-6 border border-[#0a0a0a]/[0.06] bg-white relative overflow-hidden"
              >
                <div
                  className="text-[64px] font-display font-black leading-none absolute -top-3 end-4 opacity-[0.08]"
                  style={{ color: "#c9a55c" }}
                >
                  {s.num}
                </div>
                <div className="relative">
                  <div
                    className="inline-block text-[11px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full mb-3"
                    style={{
                      background: "rgba(201,165,92,0.12)",
                      color: "#8a6f2e",
                    }}
                  >
                    {s.num}
                  </div>
                  <div className="font-display font-bold text-lg mb-1.5">
                    {lang === "ar" ? s.ar : s.en}
                  </div>
                  <div className="text-[13px] text-[#525252] leading-relaxed">
                    {lang === "ar" ? s.arBody : s.enBody}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ────────────────────────────────────────────── */}
      <section
        className="py-16 sm:py-24"
        style={{
          background:
            "linear-gradient(160deg,#0a0a0a 0%,#141110 45%,#1f1614 100%)",
        }}
      >
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 lg:px-12 text-center relative">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold mb-6"
            style={{
              background: "rgba(201,165,92,0.12)",
              border: "1px solid rgba(201,165,92,0.35)",
              color: "#e8c873",
            }}
          >
            <Sparkles className="w-3.5 h-3.5" />
            {lang === "ar" ? "افتح مساحة عملك الآن" : "Launch your workspace"}
          </div>
          <h2 className="text-4xl sm:text-6xl font-display font-black text-[#f7eecd] tracking-tight mb-5 leading-[1.05]">
            {lang === "ar"
              ? "جاهز لتحويل نشاطك رقميًا؟"
              : "Ready to go digital?"}
          </h2>
          <p className="text-[#f7eecd]/75 text-[16px] mb-8 max-w-2xl mx-auto">
            {lang === "ar"
              ? "انضم إلى المعارض، الورش، شركات التمويل، وخدمات BNPL التي تنمو مع كرتال."
              : "Join dealers, workshops, finance companies, and BNPL providers already growing on CARTAL."}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to={primaryCta}
              className="inline-flex items-center gap-2 rounded-full h-12 px-7 text-sm font-bold shadow-xl hover:scale-[1.02] transition-transform"
              style={{
                background: "linear-gradient(135deg,#e8c873,#c9a55c)",
                color: "#0a0a0a",
              }}
              data-testid="cta-register-business-bottom"
            >
              {lang === "ar" ? "أنشئ نشاطي الآن" : "Create my business"}
              <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full h-12 px-6 text-sm font-semibold border transition-colors"
              style={{
                borderColor: "rgba(247,238,205,0.25)",
                color: "#f7eecd",
              }}
              data-testid="cta-contact"
            >
              {lang === "ar" ? "تواصل مع المبيعات" : "Talk to sales"}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
